import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, qs, type ChannelStatus, type OrdersImportResponse, type PurchaseSummary } from '../api';
import { NewOrderForm } from '../components/NewOrderForm';
import { PurchaseForm, type PurchaseFormValues } from '../components/PurchaseForm';
import { statusLabel, statusTone } from '../lib/labels';
import { Badge, Button, Card, Empty, ErrorMessage, Field, Input, Notice, PageHeader, Select, Spinner, money, number, plural, when } from '../ui';

const FILTERS = [
  ['all', 'Everything'],
  ['coming_in', 'Coming in'],
  ['ordered', 'Ordered'],
  ['in_transit', 'In transit'],
  ['partially_received', 'Partly received'],
  ['received', 'Received'],
  ['closed', 'Closed'],
  ['cancelled', 'Cancelled'],
] as const;

export function Purchases() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const status = FILTERS.some(([k]) => k === params.get('status')) ? params.get('status')! : 'all';
  const partOuts = params.get('source') === 'partout';
  const list = useQuery({
    queryKey: ['purchases', status, partOuts],
    queryFn: () => api<{ purchases: PurchaseSummary[] }>(`/api/v1/purchases${qs({ status, source: partOuts ? 'partout' : undefined })}`).then((r) => r.purchases),
  });

  const create = useMutation({
    mutationFn: (v: PurchaseFormValues) => api<{ purchase: { id: number } }>('/api/v1/purchases', { body: v }),
    onSuccess: ({ purchase }) => {
      void qc.invalidateQueries({ queryKey: ['purchases'] });
      navigate(`/purchases/${purchase.id}`);
    },
  });

  return (
    <>
      <PageHeader
        title={partOuts ? 'Part-outs' : 'Purchases'}
        subtitle={partOuts ? 'Sets and lots you have broken down into stock. Open a record to continue counting or review what came in.' : 'Orders on their way, and everything already in hand. Goods only become stock when you receive them against the order.'}
        actions={
          !partOuts && <div className="flex gap-2">
            <Button
              onClick={() => {
                setOrdering((v) => !v);
                setCreating(false);
              }}
            >
              {ordering ? 'Cancel' : 'New purchase order'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setCreating((v) => !v);
                setOrdering(false);
              }}
            >
              {creating ? 'Cancel' : 'New purchase'}
            </Button>
          </div>
        }
      />
      <div className="space-y-4">
        {!partOuts && ordering && (
          <Card title="New purchase order">
            <p className="mb-3 text-sm text-slate-600">For an order that did not come from BrickLink, such as eBay. It is recorded as on its way and changes no stock until you receive it.</p>
            <NewOrderForm onCreated={(id) => navigate(`/purchases/${id}`)} />
          </Card>
        )}
        {!partOuts && creating && (
          <Card title="New purchase">
            <p className="mb-3 text-sm text-slate-600">For goods you already have, such as a car boot or a collection you collected. Orders from BrickLink come in below.</p>
            <PurchaseForm submitLabel="Create purchase" busy={create.isPending} onSubmit={(v) => create.mutate(v)} />
            <div className="mt-3">
              <ErrorMessage error={create.error} />
            </div>
          </Card>
        )}

        {!partOuts && <BricklinkOrders />}

        <div className="flex items-end gap-3">
          <Field label="Show">
            <Select
              value={status}
              onChange={(e) => {
                const next = new URLSearchParams(params);
                if (e.target.value === 'all') next.delete('status');
                else next.set('status', e.target.value);
                setParams(next, { replace: true });
              }}
            >
              {FILTERS.map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {list.isPending ? (
          <Spinner />
        ) : list.error ? (
          <ErrorMessage error={list.error} />
        ) : list.data.length === 0 ? (
          <Empty>{status === 'all' ? 'No purchases yet. Pull your BrickLink orders, or record what you bought and add its stock.' : 'Nothing here.'}</Empty>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="data w-full min-w-[52rem] text-sm">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Seller</th>
                  <th>Status</th>
                  <th className="text-right">Cost</th>
                  <th className="text-right">On order</th>
                  <th className="text-right">Received</th>
                  <th className="text-right">Left</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50">
                    <td className="whitespace-nowrap">{p.purchasedOn}</td>
                    <td>
                      <Link to={`/purchases/${p.id}`} className="font-medium hover:underline">
                        {p.description || `Purchase ${p.id}`}
                      </Link>{' '}
                      {p.isSystem && <Badge>Opening stock</Badge>}
                      {p.source === 'bricklink' && <Badge tone="blue">BrickLink</Badge>}
                      {p.source === 'partout' && <Badge tone="amber">Part-out</Badge>}
                    </td>
                    <td>
                      {p.sellerName ? (
                        p.supplierId ? (
                          <Link to={`/suppliers/${p.supplierId}`} className="hover:underline">
                            {p.sellerName}
                          </Link>
                        ) : (
                          p.sellerName
                        )
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td>
                      <Badge tone={statusTone(p.status)}>{statusLabel(p.status)}</Badge>
                    </td>
                    <td className="text-right tabular-nums">{p.isSystem ? <span className="text-slate-400">unknown</span> : money(p.totalCost, p.currency)}</td>
                    <td className="text-right tabular-nums">{p.piecesOnOrder > 0 ? number(p.piecesOnOrder) : <span className="text-slate-400">0</span>}</td>
                    <td className="text-right tabular-nums">{number(p.piecesBought)}</td>
                    <td className="text-right tabular-nums">{number(p.piecesRemaining)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

interface OrdersSettings {
  ordersFrom?: string;
  ordersLastPullAt?: string;
  ordersLastStatus?: string;
}

/** Pulls the orders you placed on BrickLink in as purchase orders. Read-only: it never receipts anything. */
function BricklinkOrders() {
  const qc = useQueryClient();
  const channels = useQuery({ queryKey: ['channels'], queryFn: () => api<{ channels: ChannelStatus[] }>('/api/v1/channels').then((r) => r.channels) });
  const bl = channels.data?.find((c) => c.kind === 'bricklink');
  const settings = (bl?.settings ?? {}) as OrdersSettings;
  const [from, setFrom] = useState('');
  const date = from || settings.ordersFrom || new Date(Date.now() - 30 * 86_400_000).toISOString().slice(0, 10);

  const pull = useMutation({
    mutationFn: () => api<OrdersImportResponse>('/api/v1/bricklink/orders/import', { body: { from: date } }),
    onSuccess: () => {
      for (const k of ['purchases', 'channels', 'dashboard', 'items']) void qc.invalidateQueries({ queryKey: [k] });
    },
  });
  const r = pull.data?.result;

  if (channels.isPending) return null;
  if (!bl || bl.mode === 'off' || !bl.configured) {
    return (
      <Notice>
        To bring in the orders you place on BrickLink,{' '}
        <Link to="/channels" className="font-medium underline">
          connect BrickLink
        </Link>{' '}
        first. It is read-only, and an order only becomes stock when you receive it here.
      </Notice>
    );
  }

  return (
    <Card title="Orders you have placed on BrickLink">
      <p className="mb-3 text-sm text-slate-600">
        Orders you place with other stores are brought in as purchase orders, with their seller, prices, postage and tracking, and counted as on order until you receive them. Nothing changes on BrickLink, and nothing becomes stock until you receive it.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Bring in orders placed from" hint={settings.ordersFrom ? undefined : 'Older orders are left out, so past orders do not show up as stock on its way.'}>
          <Input type="date" value={date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Button disabled={pull.isPending || !date} onClick={() => pull.mutate()}>
          {pull.isPending ? 'Asking BrickLink…' : settings.ordersFrom ? 'Check for new orders' : 'Pull my orders'}
        </Button>
        <span className="pb-2 text-xs text-slate-500">
          {settings.ordersFrom ? (
            <>
              Checked automatically every six hours. Last checked {when(settings.ordersLastPullAt)}
              {settings.ordersLastStatus ? ` (${settings.ordersLastStatus})` : ''}.
            </>
          ) : (
            'Once a date is set, BrickLink is checked automatically every six hours.'
          )}
        </span>
      </div>
      <div className="mt-3 space-y-2">
        <ErrorMessage error={pull.error} />
        {r && (
          <Notice tone={r.skipped.length > 0 || r.stoppedBecause ? 'amber' : 'green'}>
            Brought in {plural(r.created, 'new order')}, updated {r.updated}, {r.unchanged} unchanged
            {r.cancelledSkipped > 0 && `, ${plural(r.cancelledSkipped, 'cancelled order')} left out`}
            {r.itemsCreated > 0 && `. ${plural(r.itemsCreated, 'new item')} added to the catalogue`}.
            {r.arrivedNotReceipted > 0 && ` BrickLink says ${plural(r.arrivedNotReceipted, 'order')} ${r.arrivedNotReceipted === 1 ? 'has' : 'have'} arrived: open ${r.arrivedNotReceipted === 1 ? 'it' : 'them'} and press Receive goods.`}
            {r.stoppedBecause && ` Stopped early: ${r.stoppedBecause}`}
            {r.skipped.length > 0 && (
              <ul className="mt-1 list-disc pl-5 text-xs">
                {r.skipped.slice(0, 6).map((s) => (
                  <li key={s.orderId}>
                    Order {s.orderId} was skipped: {s.reason}
                  </li>
                ))}
              </ul>
            )}
          </Notice>
        )}
      </div>
    </Card>
  );
}
